import React, { useContext, useState, useEffect } from 'react';
import { Row, Col, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import 'assets/styles/common.scss';
import ToastNotification from 'components/common/Toast';
import { LocaleContext } from 'routes/Router';
import {
  providerAdminDashboard,
  providerOnboarding,
  superAdminDashboard,
  login,
} from 'routes/AppUrls';
import { FormattedMessage, useIntl } from 'react-intl';
import ReusableButton from 'components/common/Button';
import { useDispatch, useSelector } from 'react-redux';
import { getUserRole } from 'utils/general';
import { userMeCallAPI } from 'store/UserMeDetails/action';
import CircularProgress from '@mui/material/CircularProgress';
import OtpInput from 'components/common/Input/OtpInput';
import { verifyOtpCallAPI } from 'store/VerifyOtp/action';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { sendOtpCallAPI } from 'store/SendOtp/action';
import AuthLayout from 'components/common/AuthLayout';
import Ptext from 'components/common/Ptext';
import loginHero from 'assets/images/login/loginHero.png';
import LoginLogo from 'assets/images/login/LogoAlt.svg';
import 'assets/styles/auth.scss';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { locale } = useContext(LocaleContext);
  const [conf, setConf] = ToastNotification();
  const [timer, setTimer] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [otp, setOtp] = useState('');
  const [isValid, setIsValid] = useState(false);
  const loginLoader = useSelector((state) => state?.verifyOtpReducer?.loading);
  const sendOtpData = useSelector((state) => state?.sendOtpReducer?.data);

  useEffect(() => {
    let interval;

    if (isResendDisabled && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
    }

    return () => clearInterval(interval);
  }, [timer, isResendDisabled]);

  const handleResend = () => {
    let body = {
      email: sendOtpData?.data?.email,
    };

    dispatch(
      sendOtpCallAPI(body, {
        run: true,
        toast: false,
        handleToastError: (res) => ToastErrorFunction(res),
        navigate: navigate,
      })
    );

    setTimer(30);
    setIsResendDisabled(true);
  };

  const handleSubmit = () => {
    let body = {
      email: sendOtpData?.data?.email,
      otp: otp,
    };
    dispatch(
      verifyOtpCallAPI(body, {
        run: true,
        handleFunction: (res) => handleRedirect(res),
        handleToastError: (res) => ToastErrorFunction(res),
        navigate: navigate,
        toast: true,
      })
    );
  };

  const handleVerifyOtp = () => {
    if (isValid) {
      handleSubmit();
    } else {
      setConf({ msg: 'Please enter correct OTP', variant: 'error' });
    }
  };

  const getUserMeApi = () => {
    dispatch(
      userMeCallAPI(
        {},
        {
          run: true,
          handleFunction: (res) => handleUserMeSuccess(res),
          navigate: navigate,
        }
      )
    );
  };

  const handleUserMeSuccess = (res) => {
    if (getUserRole() === 'super-admin') {
      navigate(superAdminDashboard(locale));
    } else {
      if (res?.data?.data?.provider_details?.address) {
        navigate(providerAdminDashboard(locale));
      } else {
        navigate(providerOnboarding(locale));
      }
    }
  };

  const RoleBaseNavigate = () => {
    getUserMeApi();
  };

  const ToastErrorFunction = (res) => {
    setConf({
      msg: intl.formatMessage({ id: 'Invalid OTP!' }),
      variant: 'error',
    });
  };

  const handleRedirect = (res) => {
    if (res?.data?.status_code === 200 && res?.data?.data !== null) {
      setConf({
        msg: intl.formatMessage({ id: 'login successfully!' }),
        variant: 'success',
      });
      RoleBaseNavigate();
    } else {
      setConf({
        msg: intl.formatMessage({
          id: 'Invalid credentials.Please try again.',
        }),
        variant: 'error',
      });
    }
  };

  const OtpValidation = (value) => {
    // Development mode: accept any OTP input for testing
    if (process.env.NODE_ENV === 'development') {
      setIsValid(value.length > 0);
    } else {
      // Production mode: require exactly 6 digits
      if (value.length > 0 && value.length === 6) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
    }
  };

  return (
    <AuthLayout
      heroImage={loginHero}
      logo={LoginLogo}
      quote="Allue transformed our salon by doubling our monthly bookings and completely eliminating no-shows"
      author="Alisa Lisa"
    >
      <div className="login-content -mt-60">
        {/* HEADER */}
        <div className="flex-row mb-4">
          <div
            onClick={() => navigate(login(locale))}
            className="w-[48px] h-[48px] -ml-10 rounded-full border border-[#D6D3D1] bg-[#F5F5F4] flex items-center justify-center cursor-pointer mb-16 hover:bg-stone-200 transition-all duration-200"
          >
            <ArrowBackIcon
              sx={{
                fontSize: 16,
                color: '#525252',
              }}
            />
          </div>

          <div>
            <Ptext variant="title" className="welcome-title">
              <FormattedMessage id="Verify Your Account" />
            </Ptext>

            <Ptext variant="body" className="welcome-subtitle mt-2">
              <FormattedMessage id="Please enter your OTP to sign in" />
            </Ptext>
          </div>
        </div>

        {/* OTP INPUT */}
        <div className="mt-5 mb-4">
          <OtpInput
            marginTop="30"
            TextFieldsProps={{
              placeholder: 0,
            }}
            validateChar={(val) => !isNaN(val)}
            value={otp}
            length={6}
            onChange={(value) => {
              setOtp(value);
              OtpValidation(value);
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleVerifyOtp();
              }
            }}
          />
        </div>

        {/* RESEND */}
        <div className="text-center mb-4">
          {isResendDisabled ? (
            <Ptext variant="body" className="text-muted">
              <FormattedMessage id="Resend in" /> {timer}s
            </Ptext>
          ) : (
            <Ptext variant="body">
              <FormattedMessage id="Did not receive otp?" />{' '}
              <span
                onClick={handleResend}
                className="forgot-password fw-semibold"
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                <FormattedMessage id="Resend Code" />
              </span>
            </Ptext>
          )}
        </div>

        {/* BUTTON */}
        <div className="mt-4">
          <ReusableButton
            fullWidth
            variant="contained"
            color="#FFFFFF"
            fontSize="16px"
            fontWeight="500"
            padding="14px"
            bgColor="#000000"
            borderRadius="999px"
            fontFamily="Poppins, sans-serif"
            loading={loginLoader}
            onClick={handleVerifyOtp}
          >
            <FormattedMessage id="Verify Account" />
          </ReusableButton>
        </div>
      </div>
    </AuthLayout>
  );
};

export default VerifyOTP;
